// Encrypted local chat archive.
//
// Every chat turn Sophia stores on this device is also written here as
// AES-GCM ciphertext, keyed by a non-extractable device key that never leaves
// IndexedDB. Nothing is uploaded: the archive exists so Skye-Net can review,
// export, or delete exactly what is stored, whenever she likes.

import { openDB, type IDBPDatabase } from "idb";
import type { ChatMessage } from "./types";
import { memoryLaw } from "./memoryTruth";

const DB_NAME = "sophia-crypt";
const DB_VERSION = 1;
const KEY_ID = "device-key-v1";

export type ArchiveKind = "chat" | "memory-truth";

interface Row {
  id: string;
  ts: number;
  kind: ArchiveKind;
  role: string;
  iv: number[];
  ct: ArrayBuffer;
  bytes: number;
}

export interface ArchiveEntry {
  id: string;
  ts: number;
  kind: ArchiveKind;
  role: string;
  bytes: number;
  text: string;
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function getCryptDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("log")) {
          const s = db.createObjectStore("log", { keyPath: "id" });
          s.createIndex("ts", "ts");
        }
        if (!db.objectStoreNames.contains("keys")) db.createObjectStore("keys");
      },
    });
  }
  return dbPromise;
}

let keyPromise: Promise<CryptoKey> | null = null;
async function deviceKey(): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = (async () => {
      const db = await getCryptDB();
      const existing = (await db.get("keys", KEY_ID)) as CryptoKey | undefined;
      if (existing) return existing;
      const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
        "encrypt",
        "decrypt",
      ]);
      await db.put("keys", key, KEY_ID);
      return key;
    })();
  }
  return keyPromise;
}

/** True once a device key exists — i.e. the archive is armed and encrypting. */
export async function isArchiveArmed(): Promise<boolean> {
  try {
    const db = await getCryptDB();
    return Boolean(await db.get("keys", KEY_ID));
  } catch {
    return false;
  }
}

async function write(kind: ArchiveKind, role: string, text: string): Promise<void> {
  if (typeof crypto === "undefined" || !crypto.subtle) return;
  try {
    const key = await deviceKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(text),
    );
    const db = await getCryptDB();
    const row: Row = {
      id: crypto.randomUUID(),
      ts: Date.now(),
      kind,
      role,
      iv: Array.from(iv),
      ct,
      bytes: ct.byteLength,
    };
    await db.put("log", row);
  } catch {
    /* archive is a courtesy layer — never break the chat */
  }
}

/** Archive one chat turn, encrypted, on this device only. */
export function archiveMessage(m: ChatMessage): void {
  void write("chat", m.role, m.content ?? "");
}

/** Snapshot the current memory/offline law so it is reviewable too. */
export function archiveMemoryTruth(): void {
  void write("memory-truth", "law", memoryLaw().trim());
}

async function decrypt(row: Row): Promise<string> {
  try {
    const key = await deviceKey();
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(row.iv) as BufferSource },
      key,
      row.ct,
    );
    return new TextDecoder().decode(pt);
  } catch {
    return "«unreadable — device key changed»";
  }
}

export async function listArchive(limit = 200, kind?: ArchiveKind): Promise<ArchiveEntry[]> {
  const db = await getCryptDB();
  let rows = (await db.getAll("log")) as Row[];
  if (kind) rows = rows.filter((r) => r.kind === kind);
  rows.sort((a, b) => b.ts - a.ts);
  rows = rows.slice(0, Math.max(1, limit));
  return Promise.all(
    rows.map(async (r) => ({
      id: r.id,
      ts: r.ts,
      kind: r.kind,
      role: r.role,
      bytes: r.bytes,
      text: await decrypt(r),
    })),
  );
}

export interface ArchiveStats {
  entries: number;
  chats: number;
  laws: number;
  bytes: number;
  oldest: number | null;
  newest: number | null;
  armed: boolean;
}

export async function archiveStats(): Promise<ArchiveStats> {
  const db = await getCryptDB();
  const rows = (await db.getAll("log")) as Row[];
  return {
    entries: rows.length,
    chats: rows.filter((r) => r.kind === "chat").length,
    laws: rows.filter((r) => r.kind === "memory-truth").length,
    bytes: rows.reduce((n, r) => n + (r.bytes || 0), 0),
    oldest: rows.length ? Math.min(...rows.map((r) => r.ts)) : null,
    newest: rows.length ? Math.max(...rows.map((r) => r.ts)) : null,
    armed: await isArchiveArmed(),
  };
}

export async function deleteArchiveEntry(id: string): Promise<void> {
  const db = await getCryptDB();
  await db.delete("log", id);
}

/** Wipe every archived turn. The device key is rotated so old bytes are dead. */
export async function deleteArchive(): Promise<void> {
  const db = await getCryptDB();
  await db.clear("log");
  await db.delete("keys", KEY_ID);
  keyPromise = null;
}

/** Plaintext JSON export, downloaded to this device only. */
export async function exportArchive(): Promise<number> {
  const entries = await listArchive(100_000);
  const blob = new Blob(
    [
      JSON.stringify(
        { kind: "sophia-encrypted-chat-archive", version: 1, exportedAt: new Date().toISOString(), entries },
        null,
        2,
      ),
    ],
    { type: "application/json" },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sophia-chat-archive-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return entries.length;
}
